<template>
  <div
    class="modal fade"
    id="modalReservationForm"
    ref="modal"
    data-bs-backdrop="static"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content border-0 shadow-lg" style="border-radius: 16px;">
        <div class="modal-header border-0 pb-2">
          <h5 class="modal-title fw-bold text-dark">
            <i
              :class="type === 'CREATE' ? 'fa-solid fa-plus-circle me-2 text-theme' : 'fa-solid fa-square-pen me-2 text-theme'"
            />
            {{ type === 'CREATE' ? $t('New Reservation') : $t('Edit Reservation') }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"/>
        </div>

        <div class="modal-body px-4 pb-4">
          <div class="row g-3">
            <!-- Customer -->
            <div class="col-md-6">
              <label class="form-label small fw-bold text-muted">
                <i class="fa-solid fa-user me-1"/>{{ $t('Customer Name') }}
                <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control shadow-none"
                :class="{ 'is-invalid': errors.name }"
                v-model.trim="form.name"
                :placeholder="$t('Enter customer name') + '...'"
              >
              <div v-if="errors.name" class="invalid-feedback d-block">
                {{ errors.name }}
              </div>
            </div>

            <div class="col-md-6">
              <label class="form-label small fw-bold text-muted">
                <i class="fa-solid fa-phone me-1"/>{{ $t('Telephone') }}
                <span class="text-danger">*</span>
              </label>
              <input
                type="tel"
                class="form-control shadow-none"
                :class="{ 'is-invalid': errors.telephone }"
                v-model.trim="form.telephone"
                placeholder="08X-XXX-XXXX"
              >
              <div v-if="errors.telephone" class="invalid-feedback d-block">
                {{ errors.telephone }}
              </div>
            </div>

            <!-- Date / Pax -->
            <div class="col-md-6">
              <label class="form-label small fw-bold text-muted">
                <i class="fa-regular fa-calendar me-1"/>{{ $t('Reservation Date') }}
                <span class="text-danger">*</span>
              </label>
              <input
                type="datetime-local"
                class="form-control shadow-none"
                :class="{ 'is-invalid': errors.reserve_at }"
                v-model="form.reserve_at"
              >
              <div v-if="errors.reserve_at" class="invalid-feedback d-block">
                {{ errors.reserve_at }}
              </div>
            </div>

            <div class="col-md-6">
              <label class="form-label small fw-bold text-muted">
                <i class="fa-solid fa-users me-1"/>{{ $t('Guests') }} (Pax)
                <span class="text-danger">*</span>
              </label>
              <div class="input-group">
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  @click="adjustPax(-1)"
                  :disabled="form.pax <= 1"
                >
                  <i class="fa-solid fa-minus"/>
                </button>
                <input
                  type="number"
                  class="form-control text-center shadow-none"
                  v-model.number="form.pax"
                  min="1"
                >
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  @click="adjustPax(1)"
                >
                  <i class="fa-solid fa-plus"/>
                </button>
              </div>
            </div>

            <!-- Table (optional) -->
            <div class="col-md-12">
              <div class="d-flex justify-content-between align-items-center">
                <label class="form-label small fw-bold text-muted mb-0">
                  <i class="fa-solid fa-chair me-1"/>{{ $t('Table') }}
                  <span class="text-muted fw-normal">({{ $t('Optional — can assign later') }})</span>
                </label>
                <button
                  v-if="form.table_id"
                  type="button"
                  class="btn btn-sm btn-link p-0 text-decoration-none"
                  @click="form.table_id = null"
                >
                  <i class="fa-solid fa-xmark me-1"/>{{ $t('Clear') }}
                </button>
              </div>
              <div class="table-pick-grid mt-2">
                <button
                  type="button"
                  v-for="t in tables"
                  :key="t.id"
                  class="table-pick"
                  :class="{
                    'selected': form.table_id === t.id,
                    'undersize': t.seat_capacity < form.pax,
                    'unavailable': t.state !== 'available' && form.table_id !== t.id
                  }"
                  :disabled="t.state !== 'available' && form.table_id !== t.id"
                  @click="form.table_id = t.id"
                >
                  <div class="fw-bold">{{ t.name }}</div>
                  <div class="small text-muted">{{ $t(t.zone) }}</div>
                  <div class="small">
                    <i class="fa-solid fa-user-group me-1"/>{{ t.seat_capacity }}
                  </div>
                </button>
              </div>
            </div>

            <!-- State (edit only) -->
            <div v-if="type === 'EDIT'" class="col-md-12">
              <label class="form-label small fw-bold text-muted">
                <i class="fa-solid fa-flag me-1"/>{{ $t('Status') }}
              </label>
              <div class="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  v-for="s in stateOptions"
                  :key="s.value"
                  class="btn btn-sm rounded-3 flex-fill"
                  :class="form.state === s.value ? ('btn-' + s.color + (s.color === 'warning' ? ' text-dark' : ' text-white')) : ('btn-outline-' + s.color)"
                  @click="form.state = s.value"
                >
                  <i :class="s.icon" class="me-1"/>{{ $t(s.label) }}
                </button>
              </div>
            </div>

            <!-- Note -->
            <div class="col-md-12">
              <label class="form-label small fw-bold text-muted">
                <i class="fa-solid fa-note-sticky me-1"/>{{ $t('Note') }}
              </label>
              <textarea
                class="form-control shadow-none"
                rows="2"
                v-model.trim="form.note"
                :placeholder="$t('e.g. Window seat, birthday cake') + '...'"
                maxlength="255"
              />
            </div>
          </div>
        </div>

        <div class="modal-footer border-0 pt-0 px-4 pb-4">
          <button type="button" class="btn btn-outline-secondary rounded-3 px-4" data-bs-dismiss="modal">
            {{ $t('Cancel') }}
          </button>
          <button
            type="button"
            class="btn btn-theme text-white fw-bold rounded-3 px-4 flex-grow-1"
            @click="handleSave"
          >
            <i class="fa-solid fa-floppy-disk me-2"/>{{ $t('Save') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'ModalReservationForm',
  props: {
    type: { type: String, default: 'CREATE' },
    reservation: { type: Object, default: null },
    tables: { type: Array, default: () => [] },
    stateOptions: { type: Array, default: () => [] }
  },
  emits: ['close', 'saved'],
  data() {
    const defaultDate = moment().add(1, 'hour').startOf('hour').format('YYYY-MM-DDTHH:mm');
    return {
      form: {
        id: null,
        name: '',
        telephone: '',
        reserve_at: defaultDate,
        pax: 2,
        table_id: null,
        state: 'pending',
        note: ''
      },
      errors: {}
    };
  },
  methods: {
    adjustPax(delta) {
      const v = (this.form.pax || 0) + delta;
      if (v >= 1) this.form.pax = v;
    },
    validate() {
      this.errors = {};
      if (!this.form.name)       this.errors.name       = this.$t('Please enter customer name.');
      if (!this.form.telephone)  this.errors.telephone  = this.$t('Please enter telephone.');
      if (!this.form.reserve_at) this.errors.reserve_at = this.$t('Please select reservation date and time.');
      return Object.keys(this.errors).length === 0;
    },
    handleSave() {
      if (!this.validate()) return;
      this.$emit('saved', { ...this.form });
    },
    fillForm() {
      if (!this.reservation) return;
      this.form = {
        id: this.reservation.id,
        name: this.reservation.name || '',
        telephone: this.reservation.telephone || '',
        reserve_at: this.reservation.reserve_at
          ? moment(this.reservation.reserve_at).format('YYYY-MM-DDTHH:mm')
          : '',
        pax: this.reservation.pax || 2,
        table_id: this.reservation.table_id || null,
        state: this.reservation.state || 'pending',
        note: this.reservation.note || ''
      };
    }
  },
  mounted() {
    if (this.type === 'EDIT') this.fillForm();
    $(this.$refs.modal).modal('show');
    $(this.$refs.modal).on('hidden.bs.modal', () => this.$emit('close'));
  }
};
</script>

<style scoped>
.table-pick-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 8px;
}

.table-pick {
  background: #fff;
  border: 2px solid #dee2e6;
  border-radius: 10px;
  padding: 8px;
  text-align: center;
  transition: all 0.15s ease;
  cursor: pointer;
}

.table-pick:hover:not(:disabled) {
  border-color: #adb5bd;
  background: #f8f9fa;
}

.table-pick.selected {
  border-color: #fd7e14;
  background: #fff5ec;
  box-shadow: 0 2px 6px rgba(253, 126, 20, 0.15);
}

.table-pick.undersize:not(.selected) {
  opacity: 0.6;
}

.table-pick.unavailable {
  opacity: 0.4;
  cursor: not-allowed;
  background: #f8f9fa;
}
</style>
